import React, { createContext, useContext, useEffect, useState } from "react";
import {
  siteConfig,
  products as mockProducts,
  whyBuy,
  howToOrder,
  faqs,
  reviews,
} from "../mock";
import { fetchSettings, fetchProducts } from "../lib/api";

const SiteContext = createContext(null);
export const useSite = () => useContext(SiteContext);

// Complete fallback so the UI never renders with undefined arrays.
const fallbackSettings = {
  ...siteConfig,
  heroTitle: "Premium 1:1 Prop Bills",
  heroHighlight: "1:1 Prop Bills",
  heroSubtitle:
    "Crafted for content creators, music videos, film & photography props. Camera-ready quality, delivered instantly and shipped 100% discreetly.",
  heroCtaText: "Shop Now",
  heroImage: "/products/100s-1.jpeg",
  disclaimer:
    "All products are prop bills intended solely for creative use — film, music videos, photography and content. They are not legal tender.",
  footerAbout:
    "Premium prop bills crafted for content creators, music videos, film and photography.",
  whyBuy,
  howToOrder,
  faqs,
  reviews: reviews.map((r) => ({ ...r, rating: 5, name: r.handle, avatar: "" })),
  shippingMethods: [
    { id: "nextday", name: "Next Day Shipping", desc: "1 business day. Tracked", price: 60 },
    { id: "2day", name: "2 Day Shipping", desc: "2 business days. Tracked", price: 30 },
    { id: "regular", name: "Regular Shipping", desc: "3-5 business days. Tracked", price: 15 },
    { id: "free", name: "Free Shipping", desc: "5-10 business days. Untracked", price: 0 },
  ],
  carriers: [
    { id: "usps", name: "USPS", logo: "https://cdn.simpleicons.org/usps/333366" },
    { id: "ups", name: "UPS", logo: "https://cdn.simpleicons.org/ups/351C15" },
    { id: "fedex", name: "FedEx", logo: "https://cdn.simpleicons.org/fedex/4D148C" },
  ],
};

export const SiteProvider = ({ children }) => {
  const [settings, setSettings] = useState(fallbackSettings);
  const [products, setProducts] = useState(mockProducts);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [s, p] = await Promise.all([fetchSettings(), fetchProducts()]);
      setSettings(s);
      setProducts(Array.isArray(p) ? p : []);
      setLoading(false);
    } catch (e) {
      setTimeout(load, 3000);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (settings?.seo?.title) document.title = settings.seo.title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta && settings?.seo?.description) meta.setAttribute("content", settings.seo.description);
  }, [settings]);

  return (
    <SiteContext.Provider value={{ settings, products, loading, reload: load }}>
      {loading ? (
        <div className="min-h-screen bg-navy flex flex-col items-center justify-center gap-5" data-testid="site-loading">
          <div className="h-14 w-14 rounded-full border-2 border-gold/20 border-t-gold animate-spin" />
          <p className="text-slate-400 text-sm tracking-widest uppercase">Loading</p>
        </div>
      ) : children}
    </SiteContext.Provider>
  );
};
